import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { templateApi, Template } from '../services/data_apis';
import { API_CONFIG } from '../config/api';
import { 
  Plus, 
  Play, 
  Calendar, 
  Hash, 
  FileText, 
  Loader2, 
  TrendingUp,
  Clock,
  BarChart3,
  Zap,
  Edit2,
  ChevronDown,
  Trash2,
  MoreHorizontal,
  AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

interface ImprovedTemplatesPageProps {
  apiKey?: string;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const ImprovedTemplatesPage: React.FC<ImprovedTemplatesPageProps> = ({
  apiKey,
  isDarkMode,
  onToggleTheme
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [uniqueTopicCount, setUniqueTopicCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helper to check if API key is available
  const isApiKeyAvailable = apiKey && apiKey.trim() !== '';
  
  // Debug: Log API key status
  console.log('🔑 Templates Page - API Key Debug:', {
    apiKeyProvided: !!apiKey,
    apiKeyLength: apiKey?.length || 0,
    isApiKeyAvailable,
    apiKeyPreview: apiKey ? `${apiKey.substring(0, 10)}...` : 'NO KEY'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get Auth0 token
      const token = await getAccessTokenSilently();
      const response = await fetch(API_CONFIG.endpoints.templates.list, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorData}`);
      }
      
      const data = await response.json();
      setTemplates(data.templates);
      setUniqueTopicCount(data.unique_topic_count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    // Validate API key before creating AI templates
    if (!apiKey || apiKey.trim() === '') {
      setError('OpenAI API key is required to create AI templates. Please set your API key in the settings.');
      return;
    }
    navigate('/templates/create');
  };

  const handleCreateBlank = () => {
    // Blank templates don't require API key for creation, but will need it for execution
    navigate('/templates/create-blank');
  };

  const handleEditTemplate = (template: Template) => {
    // Navigate to edit page with template data
    navigate('/templates/edit', { 
      state: { 
        templateId: template.id,
        templateData: template 
      } 
    });
  };

  const handleDeleteTemplate = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete || isDeleting) return;

    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      await templateApi.deleteTemplate(templateToDelete.id, token);
      
      // Refresh templates list
      await loadTemplates();
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteTemplate = () => {
    if (isDeleting) return; // Prevent closing dialog while deleting
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleExecuteTemplate = async (template: Template) => {
    // Validate API key before execution
    if (!apiKey || apiKey.trim() === '') {
      setError('OpenAI API key is required to execute templates. Please set your API key in the settings.');
      return;
    }

    try {
      // Get Auth0 token
      const token = await getAccessTokenSilently();
      const response = await fetch(API_CONFIG.endpoints.templates.execute(template.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ api_key: apiKey })
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorData}`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Navigate to research page with the template session ID
        navigate('/research', {
          state: {
            query: template.initial_query,
            apiKey: apiKey,
            researchLength: template.research_length,
            sessionId: data.session_id,
            templateExecution: true
          }
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute template');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getResearchLengthLabel = (length: string) => {
    switch (length) {
      case 'quick_scan': return 'Quick Scan';
      case 'targeted': return 'Targeted';
      case 'deep_dive': return 'Deep Dive';
      default: return length;
    }
  };

  const getResearchLengthColor = (length: string) => {
    switch (length) {
      case 'quick_scan': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'targeted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'deep_dive': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Stats calculation
  const totalQuestions = templates.reduce((acc, template) => acc + template.question_count, 0);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Research Templates
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your reusable research workflows
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleCreateNew}
              disabled={!isApiKeyAvailable}
              className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
              title={!isApiKeyAvailable ? "OpenAI API key required to create AI templates" : ""}
            >
              {!isApiKeyAvailable && <AlertCircle className="w-4 h-4 mr-2" />}
              <Plus className="w-4 h-4 mr-2" />
              Create AI Template
            </Button>
            
            <Button
              onClick={handleCreateBlank}
              variant="outline"
              className="border-sky-300 dark:border-sky-600 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/30 shadow-lg hover:shadow-xl"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Create From Blank
            </Button>
          </div>
        </div>

        {/* API Key Warning Banner */}
        {!isApiKeyAvailable && (
          <div className="mb-6">
            <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-orange-800 dark:text-orange-200">
                      OpenAI API Key Required
                    </h4>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      To create or execute AI templates, please set your OpenAI API key in the settings. 
                      You can still create and edit blank templates without an API key.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards */}
        {!loading && !error && templates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[
              {
                icon: FileText,
                label: 'Total Templates',
                value: templates.length.toString(),
                color: 'text-blue-600',
                bg: 'bg-blue-50 dark:bg-blue-950/30'
              },
              {
                icon: Hash,
                label: 'Unique Topics',
                value: uniqueTopicCount.toString(),
                color: 'text-green-600',
                bg: 'bg-green-50 dark:bg-green-950/30'
              },
              {
                icon: BarChart3,
                label: 'Total Questions',
                value: totalQuestions.toString(),
                color: 'text-sky-600',
                bg: 'bg-sky-50 dark:bg-sky-950/30'
              }
            ].map(({ icon: Icon, label, value, color, bg }) => (
              <Card key={label} className={`${bg} border-0`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {label}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {value}
                      </p>
                    </div>
                    <Icon className={`w-8 h-8 ${color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <Button
              onClick={loadTemplates}
              variant="outline"
              size="sm"
              className="mt-2 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600 mr-3" />
            <span className="text-gray-600 dark:text-gray-400 text-lg">Loading templates...</span>
          </div>
        )}

        {/* Templates Grid */}
        {!loading && templates.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                All Templates ({templates.length})
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-200 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 group">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                            {template.template_name}
                          </CardTitle>
                        </div>
                        <Badge className={`shrink-0 ${getResearchLengthColor(template.research_length)}`} variant="outline">
                          {getResearchLengthLabel(template.research_length)}
                        </Badge>
                      </div>
                      <CardDescription className="text-gray-600 dark:text-gray-400 line-clamp-3 mt-2">
                        {template.description || template.initial_query}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="space-y-4">
                        {/* Stats */}
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Hash className="w-4 h-4" />
                              <span>{template.topic_count} topics</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              <span>{template.question_count} questions</span>
                            </div>
                          </div>
                        </div>
                        
                        <Separator />
                        
                        {/* Date and Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                            <Calendar className="w-4 h-4" />
                            <span>Created {formatDate(template.created_at)}</span>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              onClick={() => handleExecuteTemplate(template)}
                              size="sm"
                              disabled={!isApiKeyAvailable}
                              className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!isApiKeyAvailable ? "OpenAI API key required to execute templates" : ""}
                            >
                              {!isApiKeyAvailable && <AlertCircle className="w-3 h-3 mr-1" />}
                              <Play className="w-4 h-4 mr-1" />
                              Execute
                            </Button>
                            <Button
                              onClick={() => handleEditTemplate(template)}
                              size="sm"
                              variant="outline"
                              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteTemplate(template)}
                              size="sm"
                              variant="outline"
                              className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && templates.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-sky-100 dark:from-blue-900/30 dark:to-sky-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-sky-600 dark:text-sky-400" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                No Templates Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                Create your first research template to build reusable, structured research workflows. 
                Templates help you save time by defining topics and questions in advance.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleCreateNew}
                  size="lg"
                  disabled={!isApiKeyAvailable}
                  className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                  title={!isApiKeyAvailable ? "OpenAI API key required to create AI templates" : ""}
                >
                  {!isApiKeyAvailable && <AlertCircle className="w-5 h-5 mr-2" />}
                  <Plus className="w-5 h-5 mr-2" />
                  Create AI Template
                </Button>
                
                <Button
                  onClick={handleCreateBlank}
                  size="lg"
                  variant="outline"
                  className="border-sky-300 dark:border-sky-600 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/30 shadow-lg hover:shadow-xl"
                >
                  <Edit2 className="w-5 h-5 mr-2" />
                  Create From Blank
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog 
          open={deleteDialogOpen} 
          onOpenChange={(open) => {
            if (!open && !isDeleting) {
              cancelDeleteTemplate();
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Template</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{templateToDelete?.template_name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelDeleteTemplate} disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDeleteTemplate}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default ImprovedTemplatesPage;